import {asText} from '@prismicio/client'
import Head from 'next/head'
import type {FaqSlice} from 'prismicio-types'
import type {FC} from 'react'
import {useMemo} from 'react'
import {sanitizeJsonLd} from 'slices/BlogPost/utils'

type FaqStructuredData = {
  '@context': string
  '@type': string
  mainEntity: {
    '@type': string
    name: string
    acceptedAnswer: {
      '@type': string
      text: string
    }
  }[]
}

type SliceWithType = {slice_type: string; items?: unknown[]}

export const extractFaqItemsFromSlices = (slices: SliceWithType[]): FaqSlice['items'] =>
  slices.filter((slice): slice is FaqSlice => slice.slice_type === 'faq').flatMap((slice) => slice.items)

export const generateAggregatedFaqStructuredData = (items: FaqSlice['items']): FaqStructuredData | null => {
  const validItems = items.filter((item) => {
    const question = item.question?.trim()
    const answer = asText(item.answer)?.trim()
    return question && answer
  })

  if (validItems.length === 0) {
    return null
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: validItems.map((item) => ({
      '@type': 'Question',
      name: item.question!.trim(),
      acceptedAnswer: {
        '@type': 'Answer',
        text: asText(item.answer).trim(),
      },
    })),
  }
}

type PageFaqSEOProps = {
  slices: SliceWithType[]
}

export const PageFaqSEO: FC<PageFaqSEOProps> = ({slices}) => {
  const structuredData = useMemo(() => {
    const faqItems = extractFaqItemsFromSlices(slices)
    return generateAggregatedFaqStructuredData(faqItems)
  }, [slices])

  if (!structuredData) {
    return null
  }

  return (
    <Head>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{__html: sanitizeJsonLd(structuredData)}}
        key="faq-structured-data"
      />
    </Head>
  )
}
